import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  BrainCircuit,
  Copy,
  Check,
  Loader2,
  Mic,
  MicOff,
  Send,
  Square,
  Volume2,
  Clock,
  User,
  Info,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScoreGauge } from '@/components/ScoreGauge';
import { FloatingSalesCockpit } from '@/components/FloatingSalesCockpit';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuxiliarVendas } from '@/hooks/useAuxiliarVendas';
import { ContinuousAudioRecorder } from '@/lib/audioRecorder';
import {
  VENDA_ETAPAS,
  VendaAnalise,
  VendaEtapa,
  TipoAnaliseIa,
  DOMAIN_LABELS,
} from '@/lib/vendaAssistant';

type PageState = {
  textInput: string;
  isProcessing: boolean;
  isListening: boolean;
  secondsRemaining: number;
  lastError: string | null;
  copiedError: boolean;
  activeDomainTab: string;
  controlsOpen: boolean;
  speedDetailsOpen: boolean;
  selectedTrechoId: string | null;
};

const initialPageState: PageState = {
  textInput: '',
  isProcessing: false,
  isListening: false,
  secondsRemaining: 90,
  lastError: null,
  copiedError: false,
  activeDomainTab: 'todos',
  controlsOpen: false,
  speedDetailsOpen: false,
  selectedTrechoId: null,
};

type FunctionErrorPayload = {
  error?: string | { message?: string };
  message?: string;
  details?: string;
};

async function getFunctionErrorMessage(
  error: unknown,
  data: FunctionErrorPayload | null,
  fallback: string
) {
  const dataError = typeof data?.error === 'string' ? data.error : data?.error?.message;
  if (dataError) return dataError;

  const functionError = error as { message?: string; context?: Response } | null;
  const response = functionError?.context;

  if (response && typeof response.clone === 'function') {
    try {
      const payload = (await response.clone().json()) as FunctionErrorPayload;
      const responseError = typeof payload.error === 'string' ? payload.error : payload.error?.message;
      const detail = responseError || payload.message || payload.details;
      if (detail) return `Erro ${response.status}: ${detail}`;
    } catch {
      try {
        const detail = await response.clone().text();
        if (detail) return `Erro ${response.status}: ${detail}`;
      } catch {
        // Usa a mensagem padrão abaixo quando a resposta não puder ser lida.
      }
    }
  }

  return functionError?.message || fallback;
}

export default function AuxiliarVendaSessao() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const { state: salesState, carregarEmpresaEUsuarios, carregarDetalhesSessao, moverEtapa } = useAuxiliarVendas(id);
  const [pageState, setPageState] = useState<PageState>(initialPageState);

  const recorderRef = useRef<ContinuousAudioRecorder | null>(null);
  const audioQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingTasksRef = useRef(0);

  const setTaskRunning = useCallback((running: boolean) => {
    pendingTasksRef.current = Math.max(0, pendingTasksRef.current + (running ? 1 : -1));
    setPageState((prev) => ({ ...prev, isProcessing: pendingTasksRef.current > 0 }));
  }, []);

  // Carga inicial dos dados da sessão e empresa sem JOINs
  useEffect(() => {
    const init = async () => {
      if (authLoading) return;
      if (!user) {
        navigate('/auth');
        return;
      }
      await carregarEmpresaEUsuarios(user.id);
      if (id) {
        await carregarDetalhesSessao(id);
      }
    };
    init();
  }, [authLoading, user, id, carregarEmpresaEUsuarios, carregarDetalhesSessao, navigate]);

  // Função para submeter trecho de conversa (texto ou transcrição de áudio) para as 5 análises
  const submitTrecho = useCallback(
    async (conteudoTexto: string, origem: 'texto' | 'audio', duracaoSegundos?: number) => {
      if (!id || !conteudoTexto.trim()) return;

      setTaskRunning(true);
      setPageState((prev) => ({ ...prev, lastError: null }));

      try {
        const { data, error } = await supabase.functions.invoke('analisar-venda', {
          body: {
            sessao_id: id,
            texto: conteudoTexto.trim(),
            origem,
            duracao_segundos: duracaoSegundos ?? (origem === 'audio' ? 60 : null),
          },
        });

        if (error || data?.error) {
          const errMessage = await getFunctionErrorMessage(
            error,
            data as FunctionErrorPayload | null,
            'Falha ao processar análise de vendas.'
          );
          setPageState((prev) => ({ ...prev, lastError: errMessage }));
          toast({
            title: 'Erro na análise de vendas',
            description: errMessage,
            variant: 'destructive',
          });
          return;
        }

        setPageState((prev) => ({ ...prev, textInput: '' }));
        await carregarDetalhesSessao(id);
      } catch (err: any) {
        const errMsg = err?.message || 'Erro inesperado na conexão com o serviço de IA.';
        setPageState((prev) => ({ ...prev, lastError: errMsg }));
        toast({
          title: 'Erro de comunicação',
          description: errMsg,
          variant: 'destructive',
        });
      } finally {
        setTaskRunning(false);
      }
    },
    [id, carregarDetalhesSessao, setTaskRunning]
  );

  // Transcreve e analisa cada bloco em fila, sem descartar o próximo enquanto o anterior processa.
  const handleAudioChunkReady = useCallback(
    (blob: Blob, duration: number) => {
      if (!id) return;

      audioQueueRef.current = audioQueueRef.current.then(async () => {
        setTaskRunning(true);
        try {
          const bytes = new Uint8Array(await blob.arrayBuffer());
          let binary = '';
          for (let offset = 0; offset < bytes.length; offset += 32768) {
            binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
          }
          const base64Data = btoa(binary);

          const { data: transData, error: transError } = await supabase.functions.invoke('transcrever-venda', {
            body: {
              sessao_id: id,
              audio_base64: base64Data,
              duracao_segundos: Math.round(duration),
            },
          });

          if (transError || !transData?.texto) {
            const errMessage = await getFunctionErrorMessage(
              transError,
              transData as FunctionErrorPayload | null,
              'Erro ao transcrever áudio.'
            );
            console.error('Erro na transcrição:', errMessage);
            setPageState((prev) => ({
              ...prev,
              lastError: errMessage,
            }));
            toast({
              title: 'Erro na transcrição',
              description: errMessage,
              variant: 'destructive',
            });
            return;
          }

          const textoTranscrito = transData.texto.trim();
          if (textoTranscrito.length > 3) {
            await submitTrecho(textoTranscrito, 'audio', Math.round(duration));
          }
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Erro ao preparar o áudio.';
          console.error('Erro ao processar áudio:', error);
          setPageState((prev) => ({ ...prev, lastError: message }));
        } finally {
          setTaskRunning(false);
        }
      });
    },
    [id, submitTrecho, setTaskRunning]
  );

  // Iniciar ou pausar gravação do microfone
  const toggleListening = async () => {
    if (pageState.isListening) {
      recorderRef.current?.stop();
      setPageState((prev) => ({ ...prev, isListening: false, secondsRemaining: 90 }));
    } else {
      const recorder = new ContinuousAudioRecorder({
        onChunkReady: (blob, dur) => {
          void handleAudioChunkReady(blob, dur);
        },
        onTick: (sec) => {
          setPageState((prev) => ({ ...prev, secondsRemaining: sec }));
        },
        onError: (err) => {
          toast({
            title: 'Microfone interrompido',
            description: err,
            variant: 'destructive',
          });
          setPageState((prev) => ({ ...prev, isListening: false, lastError: err }));
        },
        onStateChange: (recording) => {
          setPageState((prev) => ({ ...prev, isListening: recording }));
        },
      });

      recorderRef.current = recorder;
      const started = await recorder.start();
      if (started) {
        toast({
          title: 'Escuta ativada',
          description: 'Capturando áudio em blocos automáticos de 1 minuto e meio.',
        });
      }
    }
  };

  // Parar microfone se desmontar a tela ou mudar de página
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        recorderRef.current.stop();
      }
    };
  }, []);

  const copyErrorToClipboard = () => {
    if (pageState.lastError) {
      navigator.clipboard.writeText(pageState.lastError);
      setPageState((prev) => ({ ...prev, copiedError: true }));
      setTimeout(() => setPageState((prev) => ({ ...prev, copiedError: false })), 2000);
      toast({
        title: 'Copiado!',
        description: 'Detalhes do erro copiados para a área de transferência.',
      });
    }
  };

  const getDisplayedAnalysis = (type: TipoAnaliseIa): VendaAnalise | undefined => {
    if (pageState.selectedTrechoId) {
      return salesState.analises.find((a) => a.tipo === type && a.trecho_id === pageState.selectedTrechoId);
    }
    return salesState.analises.find((a) => a.tipo === type);
  };

  const selectedTrecho = salesState.trechos.find((trecho) => trecho.id === pageState.selectedTrechoId);

  const responsavelNome = salesState.usuariosMap[salesState.sessaoAtiva?.responsavel_id || '']?.nome || 'Responsável';

  if (salesState.loading || !salesState.sessaoAtiva) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  const velocimetroAnalise = getDisplayedAnalysis('velocimetro');
  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto space-y-6">
        {/* Cabeçalho da Sessão */}
        <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap bg-card/60 p-3 rounded-xl border backdrop-blur-sm text-xs">
          <FloatingSalesCockpit
            cliente={salesState.sessaoAtiva.cliente_nome}
            analises={salesState.analises}
            isListening={pageState.isListening}
            isProcessing={pageState.isProcessing}
            secondsRemaining={pageState.secondsRemaining}
            lastError={pageState.lastError}
            onToggleListening={() => { void toggleListening(); }}
          />
          <Badge variant="outline" className="shrink-0 uppercase">
            {salesState.sessaoAtiva.status}
          </Badge>
          <select
            aria-label="Alterar etapa da sessão"
            className="h-8 shrink-0 rounded border bg-background px-2 text-xs font-medium text-primary cursor-pointer"
            value={salesState.sessaoAtiva.etapa}
            onChange={(e) => {
              const sessaoId = salesState.sessaoAtiva?.id;
              if (sessaoId) void moverEtapa(sessaoId, e.target.value as VendaEtapa);
            }}
          >
            {VENDA_ETAPAS.map((stage) => (
              <option key={stage.value} value={stage.value}>
                Etapa: {stage.label}
              </option>
            ))}
          </select>
          <strong className="text-base text-foreground shrink-0">{salesState.sessaoAtiva.cliente_nome}</strong>
          {salesState.sessaoAtiva.oportunidade && (
            <span className="font-medium text-foreground shrink-0">
              Oportunidade: {salesState.sessaoAtiva.oportunidade}
            </span>
          )}
          <span className="flex items-center gap-1 shrink-0 text-muted-foreground">
            <User className="h-3.5 w-3.5 text-primary" />
            Conduzido por: <strong className="text-foreground">{responsavelNome}</strong>
          </span>
          <span className="flex items-center gap-1 shrink-0 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            Última interação: {new Date(salesState.sessaoAtiva.ultima_interacao_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Layout Principal: Coluna Esquerda (Controle de Voz + Entrada + Histórico) e Direita (Cartões de Diagnóstico) */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
          <Button
            size="icon"
            aria-label={pageState.controlsOpen ? 'Fechar controles da conversa' : 'Abrir controles da conversa'}
            onClick={() => setPageState((prev) => ({ ...prev, controlsOpen: !prev.controlsOpen }))}
            className={`lg:hidden fixed left-0 top-1/2 z-[60] h-11 w-8 rounded-l-none rounded-r-md shadow-lg transition-transform ${pageState.controlsOpen ? 'translate-x-[min(86vw,380px)]' : ''}`}
          >
            {pageState.controlsOpen ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </Button>

          {/* Coluna Esquerda */}
          <aside className={`fixed inset-y-0 left-0 z-50 w-[min(86vw,380px)] overflow-y-auto bg-background p-4 pt-16 shadow-xl transition-transform lg:sticky lg:top-20 lg:z-auto lg:w-auto lg:translate-x-0 lg:overflow-visible lg:bg-transparent lg:p-0 lg:pt-0 lg:shadow-none ${pageState.controlsOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            {/* Card de Controle de Voz / Microfone */}
            <Card className="border-2 border-primary/20 bg-card/80 backdrop-blur-md">
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Volume2 className="h-5 w-5 text-primary" />
                    Escuta do Microfone
                  </span>
                  {pageState.isListening && (
                    <span className="flex items-center gap-1.5 text-xs text-destructive font-semibold animate-pulse">
                      <span className="h-2 w-2 rounded-full bg-destructive" />
                      GRAVANDO
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2 space-y-4">
                <Button
                  className="w-full h-12 text-base font-medium shadow-md transition-all"
                  size="lg"
                  variant={pageState.isListening ? 'destructive' : 'default'}
                  onClick={toggleListening}
                >
                  {pageState.isListening ? (
                    <>
                      <Square className="mr-2 h-5 w-5 fill-current" />
                      Pausar Escuta
                    </>
                  ) : (
                    <>
                      <Mic className="mr-2 h-5 w-5" />
                       Ativar Microfone (1,5 min)
                    </>
                  )}
                </Button>

                {pageState.isListening && (
                  <div className="space-y-2 rounded-lg bg-muted/50 p-3 border">
                    <div className="flex justify-between items-center text-xs font-mono">
                       <span>Bloco Atual (1,5 min):</span>
                      <span className="font-bold text-primary text-sm">
                         {Math.floor(pageState.secondsRemaining / 60)}:{String(pageState.secondsRemaining % 60).padStart(2, '0')}
                      </span>
                    </div>
                    <Progress value={((90 - pageState.secondsRemaining) / 90) * 100} className="h-2" />
                    <p className="text-[11px] text-muted-foreground text-center">
                      O bloco será enviado e analisado automaticamente a cada 1 minuto e meio.
                    </p>
                  </div>
                )}

                <div className="relative pt-2">
                  <Label htmlFor="manualText" className="text-xs text-muted-foreground mb-1 block">
                    Ou cole/digite um trecho da conversa:
                  </Label>
                  <Textarea
                    id="manualText"
                    rows={6}
                    maxLength={30000}
                    value={pageState.textInput}
                    onChange={(e) => setPageState((prev) => ({ ...prev, textInput: e.target.value }))}
                    placeholder="Cole aqui falas do cliente ou do vendedor para análise rápida..."
                    className="resize-none pr-12 text-sm"
                  />
                  <Button
                    size="icon"
                    className="absolute bottom-3 right-3 h-8 w-8 shadow"
                    aria-label="Enviar texto para análise"
                    disabled={pageState.isProcessing || !pageState.textInput.trim()}
                    onClick={() => submitTrecho(pageState.textInput, 'texto')}
                  >
                    {pageState.isProcessing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                <div className="rounded-md bg-muted/40 p-2.5 text-[11px] text-muted-foreground flex items-start gap-2 border">
                  <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    A captura utiliza o microfone do dispositivo. Em ligações ou reuniões online, certifique-se de que a voz do cliente esteja audível no ambiente.
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Exibição de Erros para Teste Manual */}
            {pageState.lastError && (
              <Card className="border-destructive/50 bg-destructive/10">
                <CardHeader className="p-3 pb-1">
                  <CardTitle className="text-xs font-semibold text-destructive flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4" /> Falha na Análise
                    </span>
                    <Button variant="outline" size="sm" onClick={copyErrorToClipboard} className="h-6 text-[10px] gap-1">
                      {pageState.copiedError ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      Copiar erro
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-1">
                  <p className="text-xs text-destructive/90 break-words">{pageState.lastError}</p>
                </CardContent>
              </Card>
            )}

            {/* Histórico Cronológico da Conversa */}
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  <span>Histórico de Trechos ({salesState.trechos.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 max-h-72 overflow-y-auto space-y-3">
                {pageState.selectedTrechoId && (
                  <Button variant="ghost" size="sm" className="h-7 w-full text-xs" onClick={() => setPageState((prev) => ({ ...prev, selectedTrechoId: null }))}>
                    Voltar ao diagnóstico mais recente
                  </Button>
                )}
                {salesState.trechos.map((t) => (
                  <Button
                    variant="ghost"
                    key={t.id}
                    onClick={() => setPageState((prev) => ({ ...prev, selectedTrechoId: t.id, activeDomainTab: 'todos', controlsOpen: false }))}
                    className={`h-auto w-full flex-col items-stretch text-left text-xs border-l-2 pl-2.5 pr-2 py-1 space-y-1 rounded-r transition-colors ${pageState.selectedTrechoId === t.id ? 'border-primary bg-primary/10' : 'border-primary/50 bg-muted/20 hover:bg-muted/50'}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant={t.origem === 'audio' ? 'secondary' : 'outline'} className="text-[9px] uppercase px-1.5">
                        {t.origem}
                      </Badge>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(t.created_at).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <p className="text-foreground line-clamp-4 whitespace-pre-line">{t.conteudo}</p>
                  </Button>
                ))}
                {!salesState.trechos.length && (
                  <p className="text-xs text-muted-foreground text-center py-6">
                    Nenhum trecho enviado ainda. Ative o microfone ou digite uma frase para começar.
                  </p>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Coluna Direita: Painel Principal de Diagnóstico e Cartões */}
          <main className="space-y-6">
            {pageState.isProcessing && (
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center gap-3 animate-pulse shadow-sm">
                <BrainCircuit className="h-6 w-6 text-primary shrink-0 animate-spin" />
                <div>
                  <p className="font-semibold text-sm">Cinco especialistas de IA estão analisando a conversa...</p>
                  <p className="text-xs text-muted-foreground">
                     Os diagnósticos serão atualizados em paralelo. Novos áudios aguardam na fila com segurança.
                  </p>
                </div>
              </div>
            )}

            {/* Velocímetro de Vendas Destacado */}
            <Card className="border-2 border-primary/30 bg-gradient-to-br from-card via-card to-primary/5">
              <CardHeader className="p-4 pb-1">
                <CardTitle className="text-lg font-bold flex items-center justify-between">
                  <span>Velocímetro da Venda</span>
                  {velocimetroAnalise?.score !== null && velocimetroAnalise?.score !== undefined && (
                    <Badge variant="secondary" className="text-sm font-bold px-3 py-1">
                      Score: {velocimetroAnalise.score} / 100
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2">
                {velocimetroAnalise ? (
                  <div className="grid sm:grid-cols-[160px_1fr] gap-4 items-center">
                    <div className="flex flex-col items-center justify-center">
                      <ScoreGauge score={velocimetroAnalise.score || 0} />
                    </div>
                    <div className="space-y-3">
                      {velocimetroAnalise.sugestao_fala && (
                        <div className="rounded-lg bg-primary/10 border border-primary/30 p-3 text-xs">
                          <strong className="text-primary block mb-1">Dica Principal para Melhorar Agora:</strong>
                          <p className="text-foreground italic">“{velocimetroAnalise.sugestao_fala}”</p>
                        </div>
                      )}

                      <Collapsible
                        open={pageState.speedDetailsOpen}
                        onOpenChange={(open) => setPageState((prev) => ({ ...prev, speedDetailsOpen: open }))}
                      >
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-full justify-between text-xs">
                            {pageState.speedDetailsOpen ? 'Ocultar análise completa' : 'Ver análise completa'}
                            <ChevronDown className={`h-4 w-4 transition-transform ${pageState.speedDetailsOpen ? 'rotate-180' : ''}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-2 pt-2">
                          <p className="text-sm font-medium text-foreground">{velocimetroAnalise.resumo}</p>
                          {Array.isArray(velocimetroAnalise.dados?.cards) &&
                            (velocimetroAnalise.dados.cards as any[]).map((card, idx) => (
                              <div key={idx} className="rounded-lg bg-background p-3 border space-y-1">
                                <p className="text-xs font-semibold text-primary">{card.titulo || 'Critérios & Evolução'}</p>
                                <p className="text-xs text-muted-foreground whitespace-pre-line">{card.conteudo}</p>
                              </div>
                            ))}
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </div>
                ) : (
                  <div className="py-8 text-center text-muted-foreground text-sm space-y-2">
                    <AlertTriangle className="h-8 w-8 mx-auto text-amber-500/80 mb-2" />
                    <p>Envie o primeiro trecho da conversa para calcular a nota e os diagnósticos da oportunidade.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Abas e Grid dos 14 Cartões de Diagnóstico */}
            <Tabs
              value={pageState.activeDomainTab}
              onValueChange={(tab) => setPageState((prev) => ({ ...prev, activeDomainTab: tab }))}
              className="space-y-4"
            >
              {selectedTrecho && pageState.activeDomainTab === 'todos' && (
                <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2 text-xs">
                  <span>Exibindo trecho de {new Date(selectedTrecho.created_at).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}</span>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setPageState((prev) => ({ ...prev, selectedTrechoId: null }))}>Mais recente</Button>
                </div>
              )}
              <TabsList className="flex flex-wrap h-auto p-1 bg-muted/60">
                <TabsTrigger value="todos" className="text-xs">Todos os Diagnósticos</TabsTrigger>
                <TabsTrigger value="rapport" className="text-xs">Rapport & Condução</TabsTrigger>
                <TabsTrigger value="tecnicas" className="text-xs">13 Técnicas & Objeções</TabsTrigger>
                <TabsTrigger value="cliente" className="text-xs">Cliente & Decisores</TabsTrigger>
                <TabsTrigger value="proposta" className="text-xs">Proposta & Negociação</TabsTrigger>
              </TabsList>

              {/* Conteúdo das Abas: Renderiza os 4 grandes domínios com seus respectivos cards */}
              {(['todos', 'rapport', 'tecnicas', 'cliente', 'proposta'] as const).map((tabDomain) => (
                <TabsContent key={tabDomain} value={tabDomain} className="space-y-6">
                  <div className={`grid gap-4 ${tabDomain === 'todos' ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
                    {(['rapport', 'tecnicas', 'cliente', 'proposta'] as TipoAnaliseIa[])
                      .filter((type) => tabDomain === 'todos' || tabDomain === type)
                      .map((type) => {
                         const analise = getDisplayedAnalysis(type);
                         const analisesDoTipo = salesState.analises.filter((item) => item.tipo === type);
                        const isOverview = tabDomain === 'todos';

                        return (
                          <Card
                            key={type}
                            className={`border transition-colors ${isOverview ? 'cursor-pointer hover:border-primary/60 hover:bg-primary/5' : 'hover:border-primary/40'}`}
                            role={isOverview ? 'button' : undefined}
                            tabIndex={isOverview ? 0 : undefined}
                            onClick={isOverview ? () => setPageState((prev) => ({ ...prev, activeDomainTab: type })) : undefined}
                            onKeyDown={isOverview ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setPageState((prev) => ({ ...prev, activeDomainTab: type }));
                              }
                            } : undefined}
                          >
                             <CardHeader className="p-3 pb-1">
                              <CardTitle className="text-base font-semibold text-primary flex items-center justify-between">
                                <span>{DOMAIN_LABELS[type]}</span>
                                {analise && (
                                  <Badge variant="outline" className="text-[10px]">
                                    Atualizado
                                  </Badge>
                                )}
                              </CardTitle>
                            </CardHeader>
                             <CardContent className="space-y-2 p-3 pt-1">
                               {!isOverview && analisesDoTipo.length > 0 ? (
                                 analisesDoTipo.map((item) => {
                                   const trecho = salesState.trechos.find((entry) => entry.id === item.trecho_id);
                                   const itemCards = (Array.isArray(item.dados?.cards) ? item.dados.cards : []) as Array<{ titulo?: string; conteudo?: string }>;
                                   return (
                                      <div key={item.id} className="w-full border-t pt-3 space-y-2 first:border-t-0 first:pt-0">
                                       <p className="text-xs font-semibold text-primary">
                                          Trecho de {new Date(item.created_at).toLocaleString('pt-BR', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })} · {trecho?.origem === 'audio' ? 'Áudio' : 'Texto'}
                                       </p>
                                       <p className="text-sm text-foreground">{item.resumo}</p>
                                       {itemCards.map((card, index) => (
                                         <div key={`${item.id}-${index}`} className="space-y-1 pl-2 border-l-2 border-primary/30">
                                           <p className="text-sm font-bold text-foreground">{card.titulo}</p>
                                           <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{card.conteudo}</p>
                                         </div>
                                       ))}
                                       {item.sugestao_fala && (
                                         <div className="rounded-md bg-primary/10 px-2 py-1.5 border border-primary/20">
                                           <span className="text-xs font-semibold text-primary">Sugestão imediata</span>
                                           <p className="text-base font-medium leading-snug text-foreground">“{item.sugestao_fala}”</p>
                                         </div>
                                       )}
                                     </div>
                                   );
                                 })
                              ) : !analise ? (
                                <p className="text-xs text-muted-foreground py-4 text-center">
                                  Aguardando o envio do primeiro trecho da conversa...
                                </p>
                              ) : null}

                               {isOverview && analise?.sugestao_fala && (
                                 <div className="rounded-md bg-primary/10 px-2 py-1.5 border border-primary/20">
                                   <span className="text-xs font-semibold text-primary block">Sugestão de fala imediata:</span>
                                   <p className="text-base font-medium leading-snug text-foreground">“{analise.sugestao_fala}”</p>
                                </div>
                              )}
                              {isOverview && analise && (
                                <p className="flex items-center justify-end gap-1 text-[11px] font-medium text-primary">
                                  Ver análise completa <ChevronRight className="h-3.5 w-3.5" />
                                </p>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </main>
        </div>
      </div>
    </Layout>
  );
}